import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { getSiteUrl } from '@/utils/seo';
import type { ArticleData } from '@/mocks/articleContent';

function generateArticleJsonLd(article: ArticleData): string {
  const siteUrl = getSiteUrl();
  const pageUrl = `${siteUrl}/${article.slug}`;
  return JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    datePublished: '2026-08-01',
    dateModified: '2026-08-10',
    author: {
      '@type': 'Organization',
      name: 'Basetao Spreadsheet Resource Hub',
      url: siteUrl,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Basetao Spreadsheet Resource Hub',
      url: siteUrl,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': pageUrl,
    },
    url: pageUrl,
  });
}

export default function ArticlePageTemplate({ article }: { article: ArticleData }) {
  return (
    <main>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: generateArticleJsonLd(article) }}
      />
      <Navbar />

      <article className="pt-24 pb-16">
        <div className="max-w-3xl mx-auto px-4 md:px-10">
          <nav className="mb-8" aria-label="Breadcrumb">
            <ol className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap">
              <li>
                <Link to="/" className="cursor-pointer hover:text-primary-500 transition-colors">Home</Link>
              </li>
              <li>
                <i className="ri-arrow-right-s-line text-foreground-400"></i>
              </li>
              <li className="text-foreground-600">{article.category}</li>
              <li>
                <i className="ri-arrow-right-s-line text-foreground-400"></i>
              </li>
              <li className="text-foreground-700 font-medium truncate max-w-[200px]">{article.title}</li>
            </ol>
          </nav>

          <header className="mb-8">
            <span className="inline-block px-3 py-1 rounded-full bg-accent-100/70 text-accent-700 text-xs font-medium mb-4">
              {article.category}
            </span>
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 leading-tight">
              {article.title}
            </h1>
            <p className="mt-4 text-sm text-foreground-600 leading-relaxed">
              {article.description}
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                Updated {article.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                {article.readTime}
              </span>
            </div>
          </header>

          <div className="mb-8 rounded-xl overflow-hidden">
            <img
              src={article.heroImage}
              alt={article.title}
              className="w-full aspect-[2/1] object-cover object-top"
            />
          </div>

          <div className="prose-custom">
            {article.sections.map((section, idx) => {
              if (section.type === 'text') {
                return (
                  <section key={idx} className="mb-10">
                    {section.heading && (
                      <h2 className="font-heading text-xl font-bold text-foreground-900 mb-4">{section.heading}</h2>
                    )}
                    {section.paragraphs?.map((p, pIdx) => (
                      <p key={pIdx} className="text-sm text-foreground-700 leading-relaxed mb-4">{p}</p>
                    ))}
                  </section>
                );
              }

              if (section.type === 'features') {
                return (
                  <section key={idx} className="mb-10">
                    {section.heading && (
                      <h2 className="font-heading text-xl font-bold text-foreground-900 mb-4">{section.heading}</h2>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {section.items?.map((item) => (
                        <div key={item.title} className="p-4 rounded-xl bg-background-50 border border-background-200/70">
                          <div className="flex items-start gap-3">
                            <div className="w-6 h-6 flex-shrink-0 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mt-0.5">
                              <i className="ri-check-line text-sm"></i>
                            </div>
                            <div>
                              <h3 className="font-heading text-sm font-semibold text-foreground-800 mb-1">{item.title}</h3>
                              <p className="text-xs text-foreground-600 leading-relaxed">{item.desc}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                );
              }

              if (section.type === 'steps') {
                return (
                  <section key={idx} className="mb-10">
                    {section.heading && (
                      <h2 className="font-heading text-xl font-bold text-foreground-900 mb-4">{section.heading}</h2>
                    )}
                    <div className="space-y-4">
                      {section.items?.map((item, sIdx) => (
                        <div key={sIdx} className="flex gap-4 p-4 rounded-xl bg-background-50 border border-background-200/70">
                          <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100/70 text-primary-600 font-heading font-bold text-sm">
                            {String(sIdx + 1).padStart(2, '0')}
                          </div>
                          <div>
                            <h3 className="font-heading text-sm font-semibold text-foreground-800 mb-1">{item.title}</h3>
                            <p className="text-xs text-foreground-600 leading-relaxed">{item.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                );
              }

              if (section.type === 'faq') {
                return (
                  <section key={idx} className="mb-10">
                    {section.heading && (
                      <h2 className="font-heading text-xl font-bold text-foreground-900 mb-4">{section.heading}</h2>
                    )}
                    <div className="space-y-4">
                      {section.items?.map((faq) => (
                        <div key={faq.title} className="p-4 rounded-xl bg-background-50 border border-background-200/70">
                          <h3 className="font-heading text-sm font-semibold text-foreground-800 mb-2">{faq.title}</h3>
                          <p className="text-xs text-foreground-600 leading-relaxed">{faq.desc}</p>
                        </div>
                      ))}
                    </div>
                  </section>
                );
              }

              if (section.type === 'image') {
                return (
                  <section key={idx} className="mb-10">
                    <div className="rounded-xl overflow-hidden">
                      <img
                        src={section.imageUrl!}
                        alt={section.imageAlt || ''}
                        className="w-full aspect-[2/1] object-cover object-top"
                      />
                    </div>
                  </section>
                );
              }

              if (section.type === 'cta') {
                return (
                  <section key={idx} className="mb-10">
                    <div className="p-6 rounded-2xl bg-primary-50/30 border border-primary-200/50 text-center">
                      {section.heading && (
                        <h3 className="font-heading text-lg font-bold text-foreground-900 mb-2">{section.heading}</h3>
                      )}
                      {section.paragraphs?.map((p, pIdx) => (
                        <p key={pIdx} className="text-sm text-foreground-600 mb-4">{p}</p>
                      ))}
                      {section.ctaType === 'internal' ? (
                        <Link
                          to={section.ctaLink || '/'}
                          className="whitespace-nowrap cursor-pointer inline-flex items-center gap-2 px-7 py-3 rounded-full bg-primary-500 text-white text-sm font-semibold transition-all duration-300 hover:bg-primary-600"
                        >
                          {section.ctaText}
                          <i className="ri-arrow-right-line"></i>
                        </Link>
                      ) : (
                        <a
                          href={section.ctaLink || '#'}
                          target="_blank"
                          rel="noopener nofollow"
                          className="whitespace-nowrap cursor-pointer inline-flex items-center gap-2 px-7 py-3 rounded-full bg-primary-500 text-white text-sm font-semibold transition-all duration-300 hover:bg-primary-600"
                        >
                          {section.ctaText}
                          <i className="ri-external-link-line"></i>
                        </a>
                      )}
                    </div>
                  </section>
                );
              }

              return null;
            })}

            <div className="mt-12 p-6 rounded-xl bg-background-100 border border-background-200/70 text-center">
              <p className="text-xs text-foreground-500 mb-4">
                Found this guide helpful? Explore more Basetao spreadsheet resources and product categories.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/"
                  className="whitespace-nowrap cursor-pointer px-6 py-2.5 rounded-full bg-primary-500 text-white text-sm font-semibold transition-all duration-300 hover:bg-primary-600"
                >
                  Back to Homepage
                </Link>
                <Link
                  to="/basetao-spreadsheet-guide"
                  className="whitespace-nowrap cursor-pointer px-6 py-2.5 rounded-full bg-background-50 border border-background-300/60 text-foreground-700 text-sm font-semibold transition-all duration-300 hover:border-primary-300"
                >
                  Complete Guide
                </Link>
              </div>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </main>
  );
}